import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { isAdminUser } from './adminAuth';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The allowlist lives in adminAuth.ts (lowercased). Note that the allowlist
    // ships in the client bundle — a server-side admin custom claim is the
    // correct long-term fix; see the notes in adminAuth.ts.
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (isAdminUser(user)) {
        // Firebase Auth session is the source of truth; the legacy
        // `adminAuth` localStorage flag is no longer read anywhere.
        localStorage.removeItem('adminAuth');
        navigate('/admin/dashboard', { replace: true });
        return;
      }
      if (user) {
        // Signed in with a non-admin account: make the reason explicit.
        setError('Accès non autorisé. Veuillez utiliser un compte administrateur.');
      } else {
        // Signed out: clear any stale legacy flag left by older builds.
        localStorage.removeItem('adminAuth');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setLoading(true);

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      const result = await signInWithPopup(auth, provider);

      if (isAdminUser(result.user)) {
        localStorage.removeItem('adminAuth');
        navigate('/admin/dashboard', { replace: true });
      } else {
        await auth.signOut();
        setError('Accès non autorisé. Veuillez utiliser un compte administrateur.');
      }
    } catch (error) {
      console.error('Error during sign in:', error);
      setError('Erreur lors de la connexion. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"
          role="status"
          aria-label="Chargement"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-amber-50">
      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg w-full max-w-sm border border-primary/10">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center text-secondary font-heading">
          Administration
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full bg-gradient-to-r from-[#F59E0B] to-[#F97316] text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity text-base font-body flex items-center justify-center gap-2 shadow-sm disabled:opacity-50"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
            />
          </svg>
          {loading ? 'Connexion...' : 'Se connecter avec Google'}
        </button>
      </div>
    </div>
  );
};

export default AdminLogin;
